import React from 'react'

import DataBridgeSectionRowContainer from './DataBridgeSectionRowContainer'

type Props = {
  /**
   * The label to display
   */
  label: string
  /**
   * The value to display. If undefined, shows 'Missing'
   */
  value: unknown
  /**
   * Whether the component is in a loading state
   */
  isLoading?: boolean
  /**
   * Optional function to format the value before display
   */
  formatValue?: (value?: unknown) => string
  /**
   * Optional function to render an action (e.g., copy button)
   */
  actionRenderer?: (value?: unknown) => React.ReactNode
}

export default function DataBridgeKeyValue({
  label,
  value,
  isLoading = false,
  formatValue,
  actionRenderer,
}: Props) {
  const renderLabel = () => {
    if (!isLoading) {
      return (
        <p
          data-testid="DataBridgeKeyValueLabel"
          className="text-foreground-normal text-base font-medium leading-5"
        >
          {label}
        </p>
      )
    } else {
      return (
        <div
          data-testid="dataBridgeKeyValueLabelPlaceholder"
          className="bg-background-surface-secondary h-5 w-1/5 animate-pulse rounded"
        />
      )
    }
  }

  const renderValue = () => {
    if (!isLoading) {
      const adjustedValue = formatValue ? formatValue(value) : value
      const displayValue =
        typeof adjustedValue === 'string' || typeof adjustedValue === 'number'
          ? adjustedValue
          : String(adjustedValue ?? 'Missing')

      return (
        <p
          className="text-foreground-subtle text-base leading-5"
          data-testid="DataBridgeKeyValueValue"
        >
          {displayValue || 'Missing'}
        </p>
      )
    } else {
      return (
        <div
          data-testid="dataBridgeKeyValueValuePlaceholder"
          className="bg-background-surface-secondary h-5 w-2/5 animate-pulse rounded"
        />
      )
    }
  }

  return (
    <DataBridgeSectionRowContainer>
      <div className="flex flex-1 flex-col space-y-1">
        {renderLabel()}
        {renderValue()}
      </div>

      {actionRenderer && actionRenderer(value)}
    </DataBridgeSectionRowContainer>
  )
}
